import {
  TilePreviewArt,
  type Color,
  type IconName,
  type TilePreviewVariant,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

export type TilePreviewSubject = {
  key: string;
  label: string;
  type: string;
  icon: IconName;
};

/**
 * Which gallery mockup a widget preset shows. The art vocabulary is
 * domain-free and lives in the `tile-preview` particle; this map is the domain
 * half — it names our widget types. A preset whose type has no dedicated
 * mockup falls back to its own icon, and the pie/donut/heatmap/treemap keys are
 * matched by name so a custom preset of a generic type still gets its shape.
 */
function resolveVariant(preset: TilePreviewSubject): TilePreviewVariant {
  const key = preset.key.toLowerCase();
  const label = preset.label.toLowerCase();
  const type = preset.type.toLowerCase();

  // The outcome-performance presets share one type, so the KEY names the view:
  // the member table and the per-entity rows draw as a list, the tone tiles
  // as a big-number metric.
  if (type === "outcome-performance") {
    return key.includes("members") || key.includes("entities") ? "list" : "metric";
  }

  return type === "metric" ||
    type === "projects-closed" ||
    type === "projects-by-stage" ||
    type === "stat-strip" ||
    type === "stage-conversion"
    ? "metric"
    : type === "dimension-bar" ||
        type === "stage-moves" ||
        type === "stage-throughput" ||
        type === "stage-funnel"
      ? "bar"
      : type === "series-line"
        ? "line"
        : type === "dimension-battery"
          ? "battery"
          : type === "portfolio-map"
            ? "treemap"
            : type === "entity-list" ||
                type === "favorites-list" ||
                type === "team-members" ||
                type === "workflow-roster"
              ? "list"
              : type === "activity-list" || type === "most-active-users"
                ? "activity"
                : type === "note"
                  ? "note"
                  : type === "embed"
                    ? "embed"
                    : type === "type-mix"
                      ? key.includes("pie") || label.includes("pie")
                        ? "pie"
                        : "donut"
                      : key.includes("pie") || label.includes("pie")
                        ? "pie"
                        : key.includes("donut") || label.includes("donut")
                          ? "donut"
                          : key.includes("heatmap") || label.includes("heatmap")
                            ? "heatmap"
                            : key.includes("treemap") || label.includes("treemap")
                              ? "treemap"
                              : "fallback";
}

export function TilePreview({
  preset,
  color,
}: Omittable<{
  preset: TilePreviewSubject;
  color?: Color;
}>) {
  return (
    <TilePreviewArt
      variant={resolveVariant(preset)}
      color={color}
      icon={preset.icon}
      idSeed={preset.key}
    />
  );
}
